/**
 * Why a plan refused something, keyed by the backend's reason codes
 * (`services/entitlements/compatibility.py`). Copied verbatim from the phone's `PlanGateCopy`
 * (frontend/lib/core/errors/failure.dart) — keep the two in step. Unknown codes fall back to the
 * server's own message, so a new reason degrades to something truthful rather than wrong.
 */
const byCode: Record<string, string> = {
  plan_indicator_tier: 'This indicator is part of Pro.',
  // Not a plan gate: no upgrade lifts it.
  indicator_unavailable: "This indicator isn't available yet.",
  plan_timeframe_allowlist: 'This timeframe is not in your plan.',
  plan_multi_timeframe: 'Conditions spanning two timeframes are part of Pro.',
  plan_optional_rules: 'Optional conditions are part of Elite.',
  plan_strategy_limit: "You're at your plan's strategy limit.",
  plan_watchlist_limit: "You're at your plan's watchlist limit.",
  plan_exit_strategy_limit: "You're at your plan's saved-exit limit.",
  plan_parent_strategy_suspended: 'The strategy this belongs to is suspended.',
  plan_instrument_allowlist: 'This instrument is not in your plan.',
  plan_backtesting_required: "Testing isn't available on your plan.",
  // Not "you've used this week's credits": a Free account is refused a 69-credit Refine with its five
  // untouched, so the copy says what is true in both cases.
  plan_testing_credits_exhausted: "You don't have enough credits for this.",
  instrument_deactivated: 'This instrument is no longer available.',
  // Country facts, not plan gates: never render an upgrade button for these.
  instrument_unavailable_in_region: 'This instrument is not available in your country.',
  country_instrument_gate: 'This instrument is not available in your country.',
  // Instrument capability facts (docs/equities_design.md §7.7): a (strategy, instrument) pairing
  // that cannot evaluate. Not plan gates — no upgrade gives forex volume or a stock a 12h bar.
  instrument_no_volume: "This strategy uses trading volume, which this instrument doesn't have. It isn't evaluated here.",
  instrument_timeframe_unsupported: "This strategy uses a timeframe this instrument doesn't have. It isn't evaluated here.",
};

/** The reasons an application is suspended for what the instrument lacks, not for the plan. */
export const CAPABILITY_REASONS: ReadonlySet<string> = new Set(['instrument_no_volume', 'instrument_timeframe_unsupported']);

/** The server's refusal when weekly credits plus the wallet cannot pay (`EntitlementGuard.ensure_can_spend`). */
export const CREDITS_EXHAUSTED = 'plan_testing_credits_exhausted';

export function planGateCopy(code: string | null | undefined, fallback: string): string {
  return (code && byCode[code]) || fallback;
}

/** Whether an upgrade could lift this lock. Country and volume facts cannot be bought away. */
export function isUpgradeable(code: string | null | undefined): boolean {
  return Boolean(code?.startsWith('plan_')) && code !== 'plan_parent_strategy_suspended';
}

/**
 * The short tag for a suspended application: "Off plan" only when a plan is the reason. A forex pair
 * a volume strategy cannot read is not off anyone's plan, and calling it that would sell an upgrade
 * that changes nothing (docs/equities_design.md §7.7).
 */
export function suspensionTag(code: string | null | undefined): string {
  return isUpgradeable(code) ? 'Off plan' : 'Not evaluated';
}
